import {
  Autocomplete,
  Checkbox,
  Chip,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { AppRoleEnum } from '@ulysses-inc/harami_api_client'
import type { FormikProps } from 'formik'
import type { ReactElement } from 'react'
import type { UserForm } from './EditUser'

interface OptionType {
  value: number | string
  label: string
  icon?: ReactElement
}

export const getRenderSelect = ({
  values,
  setFieldValue,
  errors,
  touched,
}: FormikProps<UserForm>) => {
  return function EditFormControlSelectWrapper(
    key: keyof UserForm,
    label: string,
    options: [number | string, string, ReactElement?][],
    defaultValue: string[] | AppRoleEnum = [],
    isMultiple?: boolean,
  ) {
    const optionsData: OptionType[] = options.map(([v, n, i]) => ({
      value: v,
      label: n,
      icon: i,
    }))

    const fieldId = `edit-user-${String(key)}`

    if (isMultiple) {
      const currentValues = values[key]
      const selectedOptions: OptionType[] = optionsData.filter(opt =>
        Array.isArray(currentValues)
          ? (currentValues as (number | string)[])
              .map(String)
              .includes(String(opt.value))
          : Array.isArray(defaultValue)
            ? (defaultValue as (number | string)[])
                .map(String)
                .includes(String(opt.value))
            : false,
      )

      return (
        <Stack>
          <Typography
            variant="subtitle1"
            sx={{ mb: 1 }}
            component="label"
            htmlFor={fieldId}
          >
            {label}
          </Typography>
          <Autocomplete<OptionType, true, false, false>
            multiple
            id={fieldId}
            disableCloseOnSelect
            options={optionsData}
            getOptionLabel={option => option.label}
            value={selectedOptions}
            onChange={(_, newValue) => {
              setFieldValue(
                key,
                newValue.map(opt => opt.value),
              )
            }}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderOption={(props, option, { selected }) => {
              // eslint-disable-next-line react/prop-types
              const { key: optKey, ...rest } = props
              return (
                <li key={optKey} {...rest}>
                  <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                    <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
                    {option.icon && (
                      <Stack sx={{ display: 'flex', alignItems: 'center' }}>
                        {option.icon}
                      </Stack>
                    )}
                    {option.label}
                  </Stack>
                </li>
              )
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index })
                return (
                  <Chip
                    key={key}
                    size="small"
                    variant="pale"
                    icon={option.icon}
                    label={option.label}
                    {...tagProps}
                  />
                )
              })
            }
            renderInput={params => <TextField {...params} type="search" />}
          />
        </Stack>
      )
    } else {
      // 単一選択の場合は Select を使用
      return (
        <Stack>
          <Typography
            variant="subtitle1"
            sx={{ mb: 1 }}
            component="label"
            htmlFor={fieldId}
          >
            {label}
          </Typography>
          <Select
            id={fieldId}
            value={values[key]}
            onChange={e => setFieldValue(key, e.target.value)}
            error={Boolean(errors[key] && touched[key])}
          >
            {optionsData.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {opt.icon && (
                    <Stack sx={{ display: 'flex', alignItems: 'center' }}>
                      {opt.icon}
                    </Stack>
                  )}
                  {opt.label}
                </Stack>
              </MenuItem>
            ))}
          </Select>
          {errors[key] && touched[key] && (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
              {errors[key] as string}
            </Typography>
          )}
        </Stack>
      )
    }
  }
}
